import {
  AccountData,
  Follower,
  FollowerMembershipState,
  MembershipPlanFrequency,
  MembershipPromoCode,
} from "@markit/common.types";
import { useCallback, useMemo, useState } from "react";
import ProfileItem from "../../ProfileItem";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import ThreeDotsButton from "../../Buttons/ThreeDotsButton";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import {
  convertToMoney,
  formatNumericDate,
  GetDateWithTime,
} from "@markit/common.utils";
import MembershipSubscriptionHistoryModal from "../../Subscription/Memberships/MembershipSubscriptionHistoryModal";
import LabelButton from "../../Buttons/LabelButton";
import { Colors } from "../../../utils/colors";
import {
  getUserData,
  getUserMembershipPromoCodeData,
} from "../../../utils/FirebaseUtils";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";

export const MEMBERSHIP_USER_FLEX_SECTIONS = [6, 4, 4, 5];

type ProfileFollowerMembershipItemProps = {
  item: AccountData;
  setProfilePreviewSelected: (
    profilePreviewSelected: AccountData | undefined
  ) => void;
  fetchedFollowerData: Follower[];
};

const ProfileFollowerMembershipItem = ({
  item,
  setProfilePreviewSelected,
  fetchedFollowerData,
}: ProfileFollowerMembershipItemProps) => {
  const { accountData } = useSelector(getAccountState).account;
  const [subscriptionHistoryVisible, setSubscriptionHistoryVisible] =
    useState(false);

  const [membershipPromoCode, setMembershipPromoCode] = useState<
    MembershipPromoCode | undefined
  >();
  const [groupOwner, setGroupOwner] = useState<AccountData>();
  const viewProfilePreview = useCallback(() => {
    setProfilePreviewSelected(item);
  }, [setProfilePreviewSelected, item]);

  const followerData = useMemo(
    () => fetchedFollowerData.find((follower) => follower.uid === item.uid),
    [fetchedFollowerData, item.uid]
  );

  const membershipPlan = useMemo(
    () =>
      accountData.membership.membershipPlans.find(
        (plan) => plan.id === followerData?.membershipPlanId
      ),
    [accountData.membership.membershipPlans, followerData?.membershipPlanId]
  );

  const currentlyDiscounted = useMemo(
    () =>
      followerData &&
      membershipPromoCode &&
      (followerData.discountEndDate === "" ||
        new Date(followerData.discountEndDate) > new Date()),
    [followerData, membershipPromoCode]
  );

  const discountedPrice = useMemo(
    () =>
      membershipPlan && membershipPromoCode && currentlyDiscounted
        ? convertToMoney(
            (membershipPlan.price * (1 - membershipPromoCode.value / 100)) / 100
          )
        : convertToMoney((membershipPlan?.price ?? 0) / 100),
    [membershipPlan, currentlyDiscounted, membershipPromoCode]
  );

  useAsyncOnMount(async () => {
    if (followerData?.membershipPromoCodeId) {
      const membershipPromoCode = await getUserMembershipPromoCodeData(
        accountData.uid,
        followerData.membershipPromoCodeId
      );
      setMembershipPromoCode(membershipPromoCode);
      if (membershipPromoCode?.groupOwnerUid) {
        const groupOwner = await getUserData(membershipPromoCode.groupOwnerUid);
        setGroupOwner(groupOwner);
      }
    }
  });

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const dropdownItems: DropdownMenuItem[] = [
      {
        title: "View Profile",
        icon: "ion:person",
        key: "view",
        onPress: viewProfilePreview,
      },
      {
        title: "Subscription History",
        icon: "ion:hourglass",
        key: "subscription_history",
        onPress: () => setSubscriptionHistoryVisible(true),
      },
    ];
    return dropdownItems;
  }, [viewProfilePreview]);

  const threeDots = useCallback(
    () => (
      <div style={{ marginRight: 20 }}>
        <DropdownMenu
          trigger={["click"]}
          placement="bottomRight"
          altWidth={200}
          dropdownItems={dropdownItems}
        >
          <ThreeDotsButton onPress={() => {}} />
        </DropdownMenu>
      </div>
    ),
    [dropdownItems]
  );

  return (
    <>
      <div>
        <div className="AlignedRow">
          <div
            style={{ marginLeft: 14, flex: MEMBERSHIP_USER_FLEX_SECTIONS[0] }}
          >
            <ProfileItem profileItem={item} onItemPress={viewProfilePreview} />
          </div>
          <div
            className="AlignedRow"
            style={{ flex: MEMBERSHIP_USER_FLEX_SECTIONS[1] }}
          >
            {followerData &&
            followerData.membershipState ===
              FollowerMembershipState.PAYMENT_FAILED ? (
              <LabelButton
                color={Colors.RED3}
                backgroundColor={Colors.WHITE1}
                text={"Payment Failed"}
              />
            ) : (
              <div className="ColumnNormal" style={{ paddingLeft: 4 }}>
                <span className="bodySubtextMedium">
                  {membershipPlan && currentlyDiscounted ? (
                    <s>{convertToMoney(membershipPlan.price / 100)}</s>
                  ) : null}{" "}
                  <span style={{ color: Colors.BLACK }}>
                    {membershipPlan
                      ? membershipPlan.billingFrequency ===
                        MembershipPlanFrequency.FOREVER
                        ? "Free"
                        : discountedPrice + "/mo"
                      : "--"}
                  </span>
                </span>
                <span className="bodySmallRegular">
                  {membershipPlan?.name || "Plan"}
                </span>
              </div>
            )}
          </div>
          <div
            className="AlignedRowSpaced"
            style={{ flex: MEMBERSHIP_USER_FLEX_SECTIONS[2] }}
          >
            <span className="bodySubtext" style={{ paddingLeft: 7 }}>
              {followerData && membershipPromoCode && currentlyDiscounted ? (
                <div className="ColumnNormal" style={{ gap: 1 }}>
                  <span className="bodySubtextMedium">
                    {groupOwner
                      ? "Redeemed Plan"
                      : `${membershipPromoCode.value}% Promo`}
                  </span>
                  {!groupOwner ? (
                    <span className="smallBodySubtext">
                      Code: {membershipPromoCode.alias}
                    </span>
                  ) : null}
                  {groupOwner ? (
                    <span className="smallBodySubtext">
                      Plan owner: {groupOwner.fullName}
                    </span>
                  ) : (
                    <span className="smallBodySubtext">
                      Discount ends:{" "}
                      {followerData.discountEndDate === ""
                        ? "Never"
                        : `${formatNumericDate(
                            new Date(followerData.discountEndDate)
                          )} `}
                    </span>
                  )}
                </div>
              ) : (
                <span className="bodySubtextMedium">No Promo</span>
              )}
            </span>
          </div>
          <div
            className="AlignedRowSpaced"
            style={{ flex: MEMBERSHIP_USER_FLEX_SECTIONS[3] }}
          >
            <span className="bodySubtext" style={{ paddingLeft: 7 }}>
              {followerData ? GetDateWithTime(followerData.createdAt) : "--"}
            </span>
            {threeDots()}
          </div>
        </div>
        <HorizontalDivider altMargin={14} />
      </div>
      {subscriptionHistoryVisible && followerData ? (
        <MembershipSubscriptionHistoryModal
          isVisible={subscriptionHistoryVisible}
          closeOnPress={() => setSubscriptionHistoryVisible(false)}
          creatorData={accountData}
          userData={item}
          followerData={followerData}
        />
      ) : null}
    </>
  );
};

export default ProfileFollowerMembershipItem;
